import readingSounds from '../../api/app/reading_sounds.json';
const sounds: Record<string, string> = readingSounds;

/** 每个可比对字符保留原文位置，让标点和换行不影响屏幕逐字着色。 */
interface TextUnit { value: string; index: number; sound: string }

const spokenDigits: Record<string, string> = { '零': '0', '〇': '0', '一': '1', '幺': '1', '二': '2', '两': '2', '三': '3', '四': '4', '五': '5', '六': '6', '七': '7', '八': '8', '九': '9' };

/** 否定含义和数字不能走同音容错，防止“无/五”“不/部”等被误认为一致。 */
const protectedCharacters = new Set(Array.from('不没未无非勿别莫否甭零〇一幺二两三四五六七八九十百千万亿0123456789'));

/** 统一书写形式并查固定字音表；原文及其位置不会被拼音替换。 */
function textUnits(text: string): TextUnit[] {
  const units: TextUnit[] = [];
  // 与服务端共用固定字音表；不枚举多音字所有读音，避免放宽匹配范围。
  Array.from(text).forEach((char, index) => {
    const sound = sounds[char] ?? '';
    for (const normalized of char.normalize('NFKC').toLowerCase()) {
      if (/[\p{P}\p{Z}\p{S}\s]/u.test(normalized)) continue;
      units.push({ value: spokenDigits[normalized] ?? normalized, index, sound });
    }
  });
  return units;
}

/** 字面优先，其次完整拼音相同；不做模糊声母、近音或编辑距离放宽。 */
function equivalent(expected: TextUnit, actual: TextUnit): boolean {
  if (expected.value === actual.value) return true;
  if (protectedCharacters.has(expected.value) || protectedCharacters.has(actual.value)) return false;
  return Boolean(expected.sound && actual.sound && expected.sound === actual.sound);
}

export interface ReadingMatch {
  confirmed: Set<number>;
  tentative: Set<number>;
  matched: number;
  total: number;
  complete: boolean;
}

/** 最长公共子序列只负责显示命中位置，最终通过仍要求全文等长、逐位匹配。 */
function alignedIndices(expected: TextUnit[], actual: TextUnit[]): Set<number> {
  const columns = actual.length + 1;
  const lengths = new Uint16Array((expected.length + 1) * columns);
  for (let i = expected.length - 1; i >= 0; i--) {
    for (let j = actual.length - 1; j >= 0; j--) {
      const position = i * columns + j;
      if (equivalent(expected[i]!, actual[j]!)) lengths[position] = lengths[position + columns + 1]! + 1;
      else lengths[position] = Math.max(lengths[position + columns]!, lengths[position + 1]!);
    }
  }
  const matched = new Set<number>();
  let i = 0;
  let j = 0;
  // 从开头回溯，同一句多次出现“本人/确认”时，优先点亮前面的字，不能跳到后半句。
  while (i < expected.length && j < actual.length) {
    if (equivalent(expected[i]!, actual[j]!)) {
      matched.add(expected[i]!.index);
      i++;
      j++;
    } else if (lengths[i * columns + j + 1]! >= lengths[(i + 1) * columns + j]!) j++;
    else i++;
  }
  return matched;
}

/** 每次基于完整快照重新计算，临时识别被修订时撤回高亮，避免进度虚增。 */
export function matchReading(expectedText: string, finalText: string, interimText = ''): ReadingMatch {
  // 限制比对规模；超长输入不会因截断而意外判为通过。
  const expected = textUnits(expectedText);
  const final = textUnits(finalText);
  const interim = textUnits(interimText);
  if (expected.length > 512 || final.length + interim.length > 2048) return { confirmed: new Set(), tentative: new Set(), matched: 0, total: expected.length, complete: false };
  const confirmed = alignedIndices(expected, final);
  // 临时片段只匹配已确认位置之后的原文，不能重新使用最终片段点亮另一处重复字。
  const confirmedEnd = Math.max(-1, ...confirmed);
  const tentative = alignedIndices(expected.filter(unit => unit.index > confirmedEnd), interim);
  const complete = expected.length > 0 && interim.length === 0 && expected.length === final.length && expected.every((unit, index) => equivalent(unit, final[index]!));
  return { confirmed, tentative, matched: confirmed.size, total: expected.length, complete };
}
